import { useState } from 'react'

import { errorMessage } from '@/shared/components/data'
import { Button, Field, Input, Select, Sheet, Textarea } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'

import { useCreateDocument } from '../api'
import { DOC_TYPE_KEY, type DocumentKind } from '../types'

export interface DocumentFormProps {
  companyId: string
  projectId?: string | null
  /** Tipo sugerido: la tab de Briefs crea briefs, la de Documentos crea notas. */
  defaultDocType?: string
  open: boolean
  onClose: () => void
}

/**
 * Alta de documento.
 *
 * `markdown` o `link`, nunca los dos: un CHECK de la base exige que esté
 * presente exactamente uno de `content_md` / `external_url`, así que el
 * formulario muestra un campo o el otro y jamás los dos.
 */
export function DocumentForm({
  companyId,
  projectId = null,
  defaultDocType = 'note',
  open,
  onClose,
}: DocumentFormProps) {
  const t = useT()
  const create = useCreateDocument()

  const [title, setTitle] = useState('')
  const [docType, setDocType] = useState(defaultDocType)
  const [kind, setKind] = useState<DocumentKind>('markdown')
  const [contentMd, setContentMd] = useState('')
  const [externalUrl, setExternalUrl] = useState('')

  if (!open) return null

  const valid = title.trim().length > 0 && (kind === 'markdown' || externalUrl.trim().length > 0)

  const submit = () => {
    create.mutate(
      {
        company_id: companyId,
        project_id: projectId,
        title: title.trim(),
        doc_type: docType,
        kind,
        // Exactamente uno de los dos, como pide la base.
        ...(kind === 'markdown'
          ? { content_md: contentMd, external_url: null }
          : { content_md: null, external_url: externalUrl.trim() }),
        // Todo nace interno. Hacerse visible al cliente es un acto explícito.
        visibility: 'internal',
      },
      { onSuccess: onClose },
    )
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={<h2 className="text-base text-text-1">{t('docs.new')}</h2>}
      footer={
        <div className="flex items-center justify-between gap-2">
          {create.isError ? (
            <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(create.error, t('docs.error.create'))}
            </span>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button onClick={onClose}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={!valid || create.isPending} onClick={submit}>
              {create.isPending ? t('docs.form.creating') : t('common.action.create')}
            </Button>
          </span>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) submit()
        }}
      >
        <Field label={t('common.label.title')} htmlFor="doc-title">
          <Input id="doc-title" value={title} onChange={(event) => setTitle(event.target.value)} />
        </Field>

        <Field label={t('docs.form.type')} htmlFor="doc-type">
          <Select
            id="doc-type"
            value={docType}
            onChange={(event) => setDocType(event.target.value)}
          >
            {Object.entries(DOC_TYPE_KEY).map(([value, key]) => (
              <option key={value} value={value}>
                {t(key)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('docs.form.kind')} htmlFor="doc-kind" hint={t('docs.form.kindHint')}>
          <Select
            id="doc-kind"
            value={kind}
            onChange={(event) => setKind(event.target.value as DocumentKind)}
          >
            <option value="markdown">{t('docs.kind.markdown')}</option>
            <option value="link">{t('docs.kind.link')}</option>
          </Select>
        </Field>

        {kind === 'markdown' ? (
          <Field label={t('docs.form.content')} htmlFor="doc-content">
            <Textarea
              id="doc-content"
              rows={8}
              value={contentMd}
              onChange={(event) => setContentMd(event.target.value)}
              placeholder={t('docs.form.contentPlaceholder')}
            />
          </Field>
        ) : (
          <Field label={t('docs.form.url')} htmlFor="doc-url">
            <Input
              id="doc-url"
              value={externalUrl}
              onChange={(event) => setExternalUrl(event.target.value)}
              placeholder="https://figma.com/file/…"
            />
          </Field>
        )}
      </form>
    </Sheet>
  )
}
